import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Package, MapPin, CreditCard, Truck } from 'lucide-react';
import { ordersAPI } from '../utils/api';

function statusColor(s) {
  const map = { pending: '#f59e0b', confirmed: '#10b981', processing: '#6366f1', shipped: '#06b6d4', delivered: '#10b981', cancelled: '#ef4444' };
  return map[s] || '#f59e0b';
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ordersAPI.getOne(id).then(res => setOrder(res.data.order)).catch(() => navigate('/orders')).finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!order) return null;

  const steps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
  const currentStep = steps.indexOf(order.status);

  return (
    <div className="container" style={{ padding: '40px 24px' }}>
      <button onClick={() => navigate('/orders')} className="btn btn-secondary btn-sm" style={{ marginBottom: 24 }}>
        <ChevronLeft size={16} /> My Orders
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne', fontSize: 32, fontWeight: 800 }}>Order #{order.orderNumber}</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: 4 }}>Placed on {new Date(order.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
        <span style={{ padding: '8px 18px', background: `${statusColor(order.status)}20`, color: statusColor(order.status), borderRadius: 12, fontWeight: 600, fontSize: 15 }}>
          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
        </span>
      </div>

      {/* Progress */}
      {order.status !== 'cancelled' && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontFamily: 'Syne', fontSize: 16, marginBottom: 20 }}>Order Progress</h3>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {steps.map((step, i) => (
              <div key={step} style={{ display: 'flex', alignItems: 'center', flex: i < steps.length - 1 ? 1 : 'none' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: i <= currentStep ? 'var(--accent)' : 'var(--bg-secondary)', border: `2px solid ${i <= currentStep ? 'var(--accent)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                    {i < currentStep ? '✓' : i + 1}
                  </div>
                  <span style={{ fontSize: 11, color: i <= currentStep ? 'var(--text-primary)' : 'var(--text-muted)', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{step}</span>
                </div>
                {i < steps.length - 1 && <div style={{ flex: 1, height: 2, background: i < currentStep ? 'var(--accent)' : 'var(--border)', margin: '0 8px', marginTop: -20 }} />}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24 }}>
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontFamily: 'Syne', fontSize: 16, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}><Package size={18} color="var(--accent)" /> Items</h3>
            {order.items.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: i < order.items.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <img src={item.thumbnail} alt={item.name} style={{ width: 60, height: 60, borderRadius: 10, objectFit: 'cover', background: 'var(--bg-secondary)' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{item.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>Qty: {item.quantity} × ${item.price.toLocaleString()}</div>
                </div>
                <div style={{ fontWeight: 700, color: 'var(--gold)' }}>${(item.price * item.quantity).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div className="card">
            <h3 style={{ fontFamily: 'Syne', fontSize: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><MapPin size={18} color="var(--accent)" /> Shipping Address</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              {order.shippingAddress.street}<br />
              {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}<br />
              {order.shippingAddress.country}
            </p>
            {order.trackingNumber && <p style={{ color: 'var(--accent)', marginTop: 12, fontSize: 14 }}>Tracking: {order.trackingNumber}</p>}
          </div>
        </div>
        <div className="card" style={{ height: 'fit-content' }}>
          <h3 style={{ fontFamily: 'Syne', fontSize: 16, marginBottom: 20 }}>Payment Summary</h3>
          {[['Items', `$${order.itemsPrice.toLocaleString()}`], ['Shipping', order.shippingPrice === 0 ? 'FREE' : `$${order.shippingPrice}`], ['Tax', `$${order.taxPrice}`]].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, color: 'var(--text-secondary)', fontSize: 14 }}>
              <span>{l}</span><span style={{ color: v === 'FREE' ? 'var(--success)' : 'var(--text-primary)' }}>{v}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: 14, fontFamily: 'Syne', fontWeight: 800, fontSize: 20 }}>
            <span>Total</span><span style={{ color: 'var(--gold)' }}>${order.totalPrice.toFixed(2)}</span>
          </div>
          <div style={{ marginTop: 16, padding: 12, background: 'var(--bg-secondary)', borderRadius: 10 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Payment: {order.paymentMethod === 'cash_on_delivery' ? '💵 Cash on Delivery' : '💳 Card'}</div>
            <div style={{ fontSize: 13, color: order.isPaid ? 'var(--success)' : 'var(--warning)', marginTop: 4 }}>
              {order.isPaid ? '✓ Paid' : '⏳ Pending Payment'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
